import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { TypeParser } from '@nkr/utils/form';
import { AppActionCreators } from '@nkr/store/index';
import { ActionCreators as AuthActionCreators } from '@nkr/store/authorization/actions';
import { toast } from 'react-toastify';
import { Fetcher } from './fetcher';
import { useEffect, useMemo, useRef } from 'react';
import { useAppHookContext } from './useAppHookContext';
import type { IRestModel, IRestResponse, RestApiHooks, SearchParams, JsonInit, RestApiListHooks, LookupApiHooks } from './types';



function hasResponse<T>(e: unknown): e is { response: IRestResponse<T> } {
    return TypeParser.isObject(e) && 'response' in e && TypeParser.isObject((e as { response: unknown }).response);
}

function hasMessage(e: unknown): e is { message?: string } {
    return TypeParser.isObject(e) && 'message' in e;
}

export class LookupApi<TData> {
    private routeParams         : Record<string, string | number | boolean | null | undefined> = {};
    private getLookupDataPath   : string;
    public  fetcher             : Fetcher;

    private dataMapper         ?: (json: Partial<TData> & Record<string, unknown>) => TData;

    constructor(url: string) {
        this.fetcher            = new Fetcher();
        this.getLookupDataPath  = url;
    }

    static createApi<TS>(url: string) {
        return new LookupApi<TS>(url);
    }

    withDataMapper(fn: (json: Partial<TData> & Record<string, unknown>) => TData): this {
        this.dataMapper         = fn;
        return this;
    }

    private applyLoadOne = (resp?: IRestResponse<TData> | any): IRestResponse<TData> | undefined => {
        const success       = resp && (resp?.success === true || resp?.isSuccess === true);
        if( !success ) return resp;
        const payload       = resp?.results ?? resp?.payload ?? resp?.data;
        const map           = this.dataMapper;
        const normalized    = (payload && map)
            ? map(payload as Partial<TData> & Record<string, unknown>)
            : (payload as TData | undefined);
        return { isSuccess: success, message: resp.message, errors: resp.errors, payload: normalized } as IRestResponse<TData> | undefined
    };

    public getInterpolatedPath(path: string): string {
        Object.entries(this.routeParams).forEach(([k, v]) => {
            const toReplace = `{${k}}`;
            path = path.replace(toReplace, v != null ? encodeURIComponent(String(v)) : '');
        });
        return path;
    }
    /* ----------------- SWR cache helpers ----------------- */

    private refreshList(showSpinner: (isVisible: boolean, message: string) => void = () => {}): Promise<IRestResponse<TData> | undefined> {
        showSpinner(true, 'Refreshing reference data...');
        return mutate(this.getLookupDataPath, undefined, { revalidate: true }).finally(() => showSpinner(false, 'Refreshing reference data...'));
    }

    /* -------------------- Hook factory -------------------- */

    private _hooks?: LookupApiHooks<TData>;

    public makeHooks(): LookupApiHooks<TData> {
        if (this._hooks) return this._hooks;

        const refresh = (showSpinner?: (isVisible: boolean, message: string) => void) => this.refreshList(showSpinner);

        // Use arrow functions so `this` is captured lexically; no `const api = this`
        const makeAuthedFetcher = (dispatch: Function) => async <R,>(url: string) => {
            try {
                const auth = await dispatch(AuthActionCreators.checkToken());
                const token = (auth && (auth.token && (auth.token as any).access_token)) as string | undefined;
                const headers: HeadersInit = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                } as any;
                if (token) (headers as any)['Authorization'] = `Bearer ${token}`;
                return this.fetcher.fetch<R>(url, { headers });
            } catch (e) {
                // Fallback without token if checkToken fails
                return this.fetcher.fetch<R>(url);
            }
        };
        
        const useFetch = <R,>(
            url: string | null,
            mapFn: (resp?: IRestResponse<R>) => IRestResponse<R> | undefined,
            labels: { loading: string; fail: string }
        ): IRestResponse<R> | undefined => {
            const { dispatch } = useAppHookContext();
            const { data, error, isLoading, isValidating } = useSWR<IRestResponse<R>>(url, makeAuthedFetcher(dispatch), {
                // Prevent unwanted refetches when clicking/focusing the window or when data is considered stale
                revalidateOnFocus: false,
                revalidateIfStale: false,
                // Keep reconnect revalidation so users regain fresh data after offline
                revalidateOnReconnect: true,
            });

            // Redux-based spinner control
            const showSpinner = !!url && (isLoading || (isValidating && !data && !error));
            const wasShowingRef = useRef(false);
            useEffect(() => {
                if (showSpinner && !wasShowingRef.current) {
                    dispatch(AppActionCreators.showLoadingAsync(labels.loading));
                    wasShowingRef.current = true;
                    return;
                }
                if (!showSpinner && wasShowingRef.current) {
                    dispatch(AppActionCreators.hideLoading());
                    wasShowingRef.current = false;
                }
            }, [showSpinner, labels.loading, dispatch]);

            // While visible, update message if it changes
            useEffect(() => {
                if (wasShowingRef.current && showSpinner) {
                    dispatch(AppActionCreators.showLoadingAsync(labels.loading));
                }
            }, [labels.loading, showSpinner, dispatch]);

            useEffect(() => {
                return () => {
                    if (wasShowingRef.current) {
                        dispatch(AppActionCreators.hideLoading());
                        wasShowingRef.current = false;
                    }
                };
            }, [dispatch]);

            // Notify errors via effects
            useEffect(() => {
                if (!error) return;
                const msg = hasMessage(error) && error.message ? error.message : 'Network error';
                toast.error(msg);
            }, [error]);

            useEffect(() => {
                if (!data || data.isSuccess !== false) return;
                toast.error(data.message || labels.fail);
            }, [data, labels.fail]);

            const source: IRestResponse<R> | undefined =
                hasResponse<R>(error) ? (error as { response: IRestResponse<R> }).response : data;

            return useMemo(() => mapFn(source), [source, mapFn]);
        };

        const useGetData = (): IRestResponse<TData> | undefined =>
            useFetch<TData>(
                this.getInterpolatedPath(this.getLookupDataPath),
                this.applyLoadOne,
                { loading: 'Loading reference data...', fail: 'Retrieval of reference data failed' }
            );

        return (this._hooks = {
            apiReference : this,
            useGetData,
            refresh
        });
    }
}
